import React, { useMemo } from 'react';
import * as S from './styles';
import { colors } from '@/constants';
import { formatCurrency } from '@/shared';
import { FaCheck } from 'react-icons/fa6';
import { MdOutlineNavigateNext } from 'react-icons/md';
import { OrderItemsProps } from '@/talons/orderStatusPage/useOrderStatusPage';
import { RiSignalWifiOffLine, RiErrorWarningLine, RiLoopRightLine } from 'react-icons/ri';

type StatusStyle = {
  icon: JSX.Element;
  backgroundColor: string;
  color: string;
  tag?: string;
};

// 1) normaliza: trim + lowercase + remove acento
const normalize = (value?: string) =>
  (value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const STYLE_DEFAULT: StatusStyle = {
  icon: <RiErrorWarningLine size={18} color={colors.brand.grafite} />,
  backgroundColor: colors.gray.gray10,
  color: colors.brand.grafite,
};

const STYLE_SUCCESS: StatusStyle = {
  icon: <FaCheck size={18} color={colors.alert.green300} />,
  backgroundColor: colors.alert.green100,
  color: colors.alert.green300,
};

const STYLE_DANGER: StatusStyle = {
  icon: <RiErrorWarningLine size={18} color={colors.brand.grafite} />,
  backgroundColor: colors.alert.red100,
  color: colors.alert.red200,
};

const STYLE_PROGRESS: StatusStyle = {
  icon: <RiLoopRightLine size={18} color={colors.brand.grafite} />,
  backgroundColor: colors.gray.gray10,
  color: colors.brand.grafite,
};

const STYLE_OFFLINE: StatusStyle = {
  icon: <RiSignalWifiOffLine size={18} color={colors.brand.grafite} />,
  backgroundColor: colors.gray.gray10,
  color: colors.brand.grafite,
  tag: ' [ SINCRONIZAR ]',
};

const STATUS_RULES: Array<{ includes: string[]; style: StatusStyle }> = [
  { includes: ['nota inutilizada', 'inutilizada'], style: STYLE_DANGER },
  { includes: ['cancelado'], style: STYLE_DANGER },

  // sucesso
  { includes: ['faturado'], style: STYLE_SUCCESS },
  { includes: ['emitido'], style: STYLE_SUCCESS },
  { includes: ['separado'], style: STYLE_SUCCESS },

  // andamento
  { includes: ['em fila de processamento', 'fila de processamento', 'processamento'], style: STYLE_PROGRESS },
  { includes: ['analise'], style: STYLE_PROGRESS },
  { includes: ['pre-separacao', 'pre separacao', 'preseparacao'], style: STYLE_PROGRESS },
  { includes: ['separacao'], style: STYLE_PROGRESS },
  { includes: ['roterizacao', 'roteirizacao'], style: STYLE_PROGRESS },
];

const resolveStyleFromApiStatus = (rawStatus?: string): StatusStyle => {
  const s = normalize(rawStatus);
  if (!s) return STYLE_DEFAULT;

  const found = STATUS_RULES.find((rule) => rule.includes.some((part) => s.includes(normalize(part))));

  return found?.style ?? STYLE_DEFAULT;
};

const OrderStatusCard = ({
  order,
  status,
  toggleModal,
  isLast,
  mappedOfflineOrders,
  showMagentoNumber,
}: {
  order: OrderItemsProps;
  status?: string;
  toggleModal?: (status: string) => void;
  isLast?: boolean;
  mappedSynchedOrders?: Map<string, any>;
  showMagentoNumber?: boolean;
  mappedOfflineOrders?: Map<string, any>;
}) => {
  // Texto exibido: EXATAMENTE o que vier da API
  const apiStatusText = status || order?.status || '';

  const statusStyle = useMemo<StatusStyle>(() => {
    // pedido offline/local
    if (mappedOfflineOrders?.get(order?.orderNumber)) return STYLE_PROGRESS;
    return resolveStyleFromApiStatus(apiStatusText);
  }, [mappedOfflineOrders, order?.orderNumber, apiStatusText]);

  const displayedStatusText = mappedOfflineOrders?.get(order?.orderNumber)
    ? 'Pendente de envio'
    : apiStatusText || 'Status não identificado';

  return (
    <React.Fragment>
      <S.SubContainer
        onClick={() => {
          if (toggleModal) return toggleModal(order?.orderNumber);
          return null;
        }}
      >
        <S.TopContainer>
          <S.IconContainer $backgroundcolor={statusStyle.backgroundColor}>{statusStyle.icon}</S.IconContainer>

          <S.OrderNumberContainer>
            <S.OrderNumber>Nº do pedido:</S.OrderNumber>
            <S.OrderNumber $color={colors.brand.grafite} $weight={700}>
              {order?.orderNumber || '----'}
            </S.OrderNumber>
          </S.OrderNumberContainer>

          <S.OrderNumberSubContainer>
            <S.OrderNumberContainer>
              <S.OrderNumber>
                {order?.erpNumber
                  ? 'Nº do pedido ERP:'
                  : !toggleModal && order?.erpNumberClient
                    ? 'Nº do pedido ERP Cliente:'
                    : 'Nº do pedido ERP:'}
              </S.OrderNumber>

              <S.OrderNumber $color={colors.brand.grafite} $weight={700}>
                {order?.erpNumber ? order?.erpNumber : !toggleModal && order?.erpNumberClient ? order?.erpNumberClient : '----'}
              </S.OrderNumber>
            </S.OrderNumberContainer>

            {toggleModal && (
              <MdOutlineNavigateNext
                color={colors.brand.grafite}
                size={24}
                style={{ marginLeft: 12 }}
                onClick={() => toggleModal(order?.orderNumber)}
              />
            )}
          </S.OrderNumberSubContainer>
        </S.TopContainer>

        <S.TopContainer>
          <div style={{ width: 32 }} />
          <S.OrderNumberContainer>
            <S.OrderNumber>Valor Total:</S.OrderNumber>
            <S.OrderNumber $color={colors.brand.grafite} $weight={700}>
              {formatCurrency({ value: order.total })}
            </S.OrderNumber>
          </S.OrderNumberContainer>

          {order?.vlr_comissao && (
            <S.OrderNumberContainer>
              <S.OrderNumber>Comissão:</S.OrderNumber>
              <S.OrderNumber $color={colors.brand.grafite} $weight={700}>
                {formatCurrency({ value: order.vlr_comissao })}
              </S.OrderNumber>
            </S.OrderNumberContainer>
          )}
        </S.TopContainer>

        <S.BottomContainer>
          <S.Divider />

          <S.BlockContainer $showMagentoNumber={showMagentoNumber}>
            <S.RegularTag $width={'fit-content'}>
              {Number(order?.quantity) > 1 ? `${order?.quantity} Itens` : `${order?.quantity} Item`}
            </S.RegularTag>

            {order?.orderNumberMagento && showMagentoNumber && (
              <S.OrderNumberContainer>
                <S.OrderNumber>Nº Magento:</S.OrderNumber>
                <S.OrderNumber $color={colors.brand.grafite} $weight={700}>
                  {order.orderNumberMagento || '----'}
                </S.OrderNumber>
              </S.OrderNumberContainer>
            )}

            <S.RegularTag $justifyEnd color={statusStyle.color} $backgroundcolor={statusStyle.backgroundColor}>
              {displayedStatusText}
              {!!statusStyle.tag && <span style={{ marginLeft: 3 }}>{statusStyle.tag}</span>}
            </S.RegularTag>
          </S.BlockContainer>
        </S.BottomContainer>

        <S.BottomContainer />
        {!isLast && <S.Divider color={colors.background1} />}
      </S.SubContainer>
    </React.Fragment>
  );
};

export default OrderStatusCard;
